<script setup>
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import { useUsersStore } from '@/stores'
import { useDesignSystem } from '@/composables/useDesignSystem'

const { t } = useI18n()
const { dialogWidth } = useDesignSystem()
const dWidth = dialogWidth('md')

const usersStore = useUsersStore()
const {
  isShowingMemberCommitmentDialog,
  memberCommitmentDialogMode,
  memberCommitmentDialogSource,
} = storeToRefs(usersStore)

const submitting = ref(false)

const isGate = computed(() => memberCommitmentDialogMode.value === 'gate')

const onAgree = async () => {
  submitting.value = true
  await usersStore.acceptMemberCommitment(memberCommitmentDialogSource.value)
  submitting.value = false
}

const onDeleteAccount = async () => {
  await usersStore.hideMemberCommitmentDialog()
  await usersStore.showDeleteAccountDialog()
}

const onCloseView = () => {
  usersStore.hideMemberCommitmentDialog()
}
</script>

<template>
  <VDialog
    :width="dWidth"
    :model-value="isShowingMemberCommitmentDialog"
    :persistent="isGate"
    scrollable
  >
    <VCard class="pa-3 pa-sm-10 ma-0">
      <VCardItem class="text-center pa-0">
        <VCardTitle class="text-h5 font-weight-medium mb-2">
          {{ t('member.commitment.title') }}
        </VCardTitle>
      </VCardItem>

      <VCardText
        class="mt-2"
        style="max-height: 60vh;"
      >
        <p
          class="mb-4"
          style="white-space: pre-line;"
        >
          {{ t('member.commitment.intro') }}
        </p>

        <div
          class="mb-4 member-commitment-bullets"
          v-html="t('member.commitment.bullets')"
        />

        <p class="mb-4">
          {{ t('member.commitment.consequences') }}
        </p>

        <p class="mb-0 font-italic">
          {{ t('member.commitment.closing') }}
        </p>
      </VCardText>

      <VCardActions
        v-if="isGate"
        class="d-flex flex-column align-center gap-3 mt-2"
      >
        <VBtn
          color="primary"
          variant="elevated"
          size="large"
          block
          :loading="submitting"
          :disabled="submitting"
          @click="onAgree"
        >
          {{ t('member.commitment.agreeButton') }}
        </VBtn>
        <VBtn
          variant="text"
          color="secondary"
          :disabled="submitting"
          @click="onDeleteAccount"
        >
          {{ t('member.commitment.deleteButton') }}
        </VBtn>
      </VCardActions>

      <VCardActions
        v-else
        class="d-flex justify-center mt-2"
      >
        <VBtn
          color="primary"
          variant="elevated"
          @click="onCloseView"
        >
          Close
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>
</template>

<style scoped>
.member-commitment-bullets :deep(ul) {
  padding-inline-start: 1.25rem;
  margin: 0;
}

.member-commitment-bullets :deep(li) {
  margin-bottom: 0.5rem;
  line-height: 1.4;
}
</style>
